import { useEffect, useRef, useCallback } from 'react';
import { checkDataFreshness, MAIN_PAGES, preloadMainPages, startPeriodicCacheRefresh } from '../services/cacheService';

export const useCacheManager = (queryKey, fetchFn, options = {}) => {
    const {
        preloadOtherPages = true,
        refreshInterval = 30, // en minutes
        onCacheHit = () => { },
        onCacheMiss = () => { },
        onCacheUpdate = () => { },
        backgroundCheck = false, // Nouveau paramètre
        onBackgroundCheckStart = () => { }, // Nouveau paramètre
        onBackgroundCheckEnd = () => { }    // Nouveau paramètre
    } = options;

    // Stocker les références des fonctions de rappel
    const callbacks = useRef({ onCacheHit, onCacheMiss, onCacheUpdate, onBackgroundCheckStart, onBackgroundCheckEnd });

    // Mettre à jour les références si les callbacks changent
    useEffect(() => {
        callbacks.current = { onCacheHit, onCacheMiss, onCacheUpdate, onBackgroundCheckStart, onBackgroundCheckEnd };
    }, [onCacheHit, onCacheMiss, onCacheUpdate, onBackgroundCheckStart, onBackgroundCheckEnd]);

    // Référence pour l'arrêt de l'actualisation périodique
    const stopRefreshRef = useRef(null);

    // Garder une trace des rendus pour éviter les initialisations multiples
    const isInitializedRef = useRef(false);

    // Fonction mémorisée pour vérifier le cache courant
    const checkCurrentCache = useCallback(async () => {
        try {
            const cacheStatus = await checkDataFreshness(queryKey, fetchFn);

            if (cacheStatus) {
                // Le cache existe et est valide
                callbacks.current.onCacheHit();
            } else {
                // Pas de cache ou cache invalide
                callbacks.current.onCacheMiss();
            }

            // Précharger d'autres pages en arrière-plan (une seule fois)
            if (preloadOtherPages && !isInitializedRef.current) {
                preloadMainPages();
                isInitializedRef.current = true;
            }
        } catch (error) {
            console.error("Erreur lors de la gestion du cache:", error);
        }
    }, [queryKey, fetchFn, preloadOtherPages]);

    // Hook d'effet principal
    useEffect(() => {
        // Exécuter la vérification initiale
        checkCurrentCache();

        // Démarrer l'actualisation périodique (une seule fois)
        if (!stopRefreshRef.current) {
            stopRefreshRef.current = startPeriodicCacheRefresh(refreshInterval);
        }

        // Nettoyage lors du démontage du composant
        return () => {
            if (stopRefreshRef.current) {
                stopRefreshRef.current();
            }
        };
    }, [checkCurrentCache, refreshInterval]);

    // Vérifier périodiquement les mises à jour en arrière-plan avec gestion de l'état
    useEffect(() => {
        const checkUpdateInterval = setInterval(() => {
            // Signaler le début de la vérification en arrière-plan
            if (backgroundCheck) {
                callbacks.current.onBackgroundCheckStart();
            }
            
            // Vérifier en arrière-plan si les données ont changé
            checkDataFreshness(queryKey, fetchFn)
                .then(updated => {
                    if (updated) {
                        callbacks.current.onCacheUpdate();
                    }
                })
                .catch(err => {
                    console.error("Erreur lors de la vérification des mises à jour:", err);
                })
                .finally(() => {
                    // Signaler la fin de la vérification en arrière-plan
                    if (backgroundCheck) {
                        callbacks.current.onBackgroundCheckEnd();
                    }
                });
        }, 2 * 60 * 1000); // Vérifier toutes les 2 minutes

        return () => clearInterval(checkUpdateInterval);
    }, [queryKey, fetchFn, backgroundCheck]);
};